/**
 * ═══════════════════════════════════════════════════════════════
 * Hasımoğlu Çiftliği – Eve Teslimat Servisi
 * ───────────────────────────────────────────────────────────────
 * 'deliveries' koleksiyonu için CRUD işlemleri.
 * Tek müşteri (telefon) = tek teslimat kaydı.
 * ═══════════════════════════════════════════════════════════════
 */

import {
  collection,
  doc,
  query,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const DELIVERY_STATUS = Object.freeze({
  PENDING: 'pending',
  ADDRESS_REQUESTED: 'address-requested',
  ADDRESS_RECEIVED: 'address-received',
  DELIVERED: 'delivered',
  CENTER_PICKUP: 'center-pickup',
  SELF_PICKUP: 'self-pickup',
});

export const subscribeDeliveries = (callback) => {
  const q = query(collection(db, 'deliveries'));
  return onSnapshot(q, (snapshot) => {
    const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    data.sort((a, b) => {
      if (typeof a.routeIndex === 'number' && typeof b.routeIndex === 'number') {
        return a.routeIndex - b.routeIndex;
      }
      if (typeof a.routeIndex === 'number') return -1;
      if (typeof b.routeIndex === 'number') return 1;

      const t1 = a.createdAt?.toMillis?.() || 0;
      const t2 = b.createdAt?.toMillis?.() || 0;
      return t2 - t1;
    });
    callback(data);
  });
};

export const addDelivery = async ({ customerPhone, customerName, branch, shareIds, shareCount, isCenterDelivery, isSelfPickup }, createdBy) => {
  if (!customerPhone) throw new Error('Telefon numarası zorunlu.');
  const deliveryRef = doc(collection(db, 'deliveries'));
  
  const centerAddress = "Mevlana Cd. No:34, Sülüntepe, 34909 Pendik/İstanbul";
  
  let status = DELIVERY_STATUS.PENDING;
  let address = '';
  let addressReceivedAt = null;

  if (isCenterDelivery) {
    status = DELIVERY_STATUS.CENTER_PICKUP;
    address = centerAddress;
    addressReceivedAt = serverTimestamp();
  } else if (isSelfPickup) {
    status = DELIVERY_STATUS.SELF_PICKUP;
    address = 'Kendi Alacak (Çiftlikten)';
    addressReceivedAt = serverTimestamp();
  }

  const payload = {
    customerPhone,
    customerName: customerName || '',
    branch: branch || null,
    shareIds: Array.isArray(shareIds) ? shareIds : [],
    shareCount: Number(shareCount) || (shareIds?.length || 0),
    status,
    address,
    note: '',
    addressRequestedAt: null,
    addressReceivedAt,
    deliveredAt: null,
    createdAt: serverTimestamp(),
    createdBy: createdBy || null,
  };
  await setDoc(deliveryRef, payload);
  return { id: deliveryRef.id, ...payload };
};

export const updateDelivery = async (deliveryId, fields) => {
  const ref = doc(db, 'deliveries', deliveryId);
  await updateDoc(ref, { ...fields, updatedAt: serverTimestamp() });
};

export const markAddressRequested = async (deliveryId) => {
  const ref = doc(db, 'deliveries', deliveryId);
  await updateDoc(ref, {
    status: DELIVERY_STATUS.ADDRESS_REQUESTED,
    addressRequestedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const setDeliveryAddress = async (deliveryId, address) => {
  const ref = doc(db, 'deliveries', deliveryId);
  const trimmed = (address || '').trim();
  await updateDoc(ref, {
    address: trimmed,
    status: trimmed ? DELIVERY_STATUS.ADDRESS_RECEIVED : DELIVERY_STATUS.PENDING,
    addressReceivedAt: trimmed ? serverTimestamp() : null,
    updatedAt: serverTimestamp(),
  });
};

export const markDelivered = async (deliveryId) => {
  const ref = doc(db, 'deliveries', deliveryId);
  await updateDoc(ref, {
    status: DELIVERY_STATUS.DELIVERED,
    deliveredAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

export const deleteDelivery = async (deliveryId) => {
  const ref = doc(db, 'deliveries', deliveryId);
  await deleteDoc(ref);
};

export const updateDeliveryRouteOrder = async (orderedIds) => {
  if (!orderedIds || !orderedIds.length) return;
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    const ref = doc(db, 'deliveries', id);
    batch.update(ref, { routeIndex: index, updatedAt: serverTimestamp() });
  });
  await batch.commit();
};
